// Super admin panel: every shop on the platform, its status, and its owner account.
import { useEffect, useState } from 'react';
import { api, useApi, dollars, fmtDate, ago, centsInput, toCents, today, type Row } from '../lib';
import { Badge, Button, Panel, Table, Loading, ErrorBox, Modal, Form, Field, MoneyField, Select, TextArea, Stat, Icon, useToast } from '../ui';

type PMe = { user: { id: string; login: string; name: string }; name: string; domain: string };

export function PlatformApp() {
  const [me, setMe] = useState<PMe | null | undefined>(undefined);
  const [setup, setSetup] = useState<{ needsSetup: boolean; name: string } | null>(null);
  async function load() {
    try { setMe(await api<PMe>('/api/platform/me')); }
    catch { setMe(null); setSetup(await api('/api/platform/setup').catch(() => ({ needsSetup: false, name: 'Asena Bay' }))); }
  }
  useEffect(() => { void load(); }, []);
  if (me === undefined) return <Loading />;
  if (!me) {
    const name = setup?.name || 'Asena Bay';
    return (
      <div className="auth platform-auth">
        <div className="auth-card">
          <div className="platform-mark"><span>{name.slice(0, 1)}</span><b>{name}</b></div>
          <h1>{setup?.needsSetup ? 'Create super admin' : 'Super admin'}</h1>
          <p>{setup?.needsSetup ? 'This account manages every shop on the platform. You need the platform setup code.' : 'Sign in to manage shops.'}</p>
          <Form submitLabel={setup?.needsSetup ? 'Create account' : 'Sign in'} onSubmit={async v => {
            await api(setup?.needsSetup ? '/api/platform/setup' : '/api/platform/login', { body: v }); await load();
          }}>
            {setup?.needsSetup && <Field label="Platform setup code" name="setupCode" required full autoComplete="off" />}
            {setup?.needsSetup && <Field label="Your name" name="name" required full />}
            <Field label="Username" name="login" required full autoCapitalize="none" autoComplete="username" />
            <Field label="Password" name="password" type="password" minLength={6} required full autoComplete={setup?.needsSetup ? 'new-password' : 'current-password'} />
          </Form>
        </div>
      </div>
    );
  }
  return <Shops me={me} onSignOut={async () => { await api('/api/platform/logout', { body: {} }); setMe(null); void load(); }} />;
}

function Shops({ me, onSignOut }: { me: PMe; onSignOut: () => void }) {
  const { data, error, reload } = useApi<Row[]>('/api/platform/shops');
  const activity = useApi<Row[]>('/api/platform/activity');
  const [modal, setModal] = useState<null | 'add' | 'mypw' | { edit: Row } | { reset: Row } | { open: Row } | { backups: Row } | { temp: { name: string; login: string; password: string } }>(null);
  const openShop = async (s: Row, mode: 'view' | 'edit') => {
    try { const r = await api<{ url: string }>(`/api/platform/shops/${s.id}/open`, { body: { mode } }); window.location.href = r.url; }
    catch (e) { toast((e as Error).message, 'err'); }
  };
  const toast = useToast();
  const shops = data || [];
  const paying = shops.filter(s => s.status === 'active');
  const mrr = paying.reduce((a, s) => a + (s.price || 0), 0);
  const address = (s: Row) => s.domain || (me.domain ? `${s.slug}.${me.domain}` : '');
  return (
    <div className="platform">
      <header className="platform-top">
        <div className="platform-mark"><span>{me.name.slice(0, 1)}</span><b>{me.name}</b><em>Super admin</em></div>
        <div className="actions"><span className="muted">{me.user.name}</span><Button kind="ghost" onClick={() => setModal('mypw')}>Change password</Button><button className="icon-btn" title="Sign out" aria-label="Sign out" onClick={onSignOut}><Icon name="logout" /></button></div>
      </header>
      <main className="content platform-main">
        <div className="page-head">
          <div><h1>Shops</h1><p>Every shop on {me.name}. Each one has its own database, logins, logo and customers.</p></div>
          <div className="actions"><Button kind="primary" icon="plus" onClick={() => setModal('add')}>Add shop</Button></div>
        </div>
        <div className="stats">
          <Stat label="Shops" value={shops.length} hint={`${shops.filter(s => s.status === 'trial').length} on trial`} />
          <Stat label="Paying shops" value={paying.length} />
          <Stat label="Monthly subscriptions" value={dollars(mrr)} hint="From shops marked active" />
          <Stat label="Paused" value={shops.filter(s => s.status === 'paused').length} />
        </div>
        <ErrorBox error={error} onRetry={reload} />
        {!data ? <Loading /> : (
          <Panel>
            <Table heads={['Shop', 'Address', 'Status', 'Plan', 'Staff', 'Open jobs', 'Billed this month', 'Last activity', '']} empty="No shops yet. Add your own shop first."
              rows={shops.map(s => [
                <><b>{s.name}</b><small className="muted">{s.owner_name}{s.stats?.ownerLogin ? <> · username <b>{s.stats.ownerLogin}</b></> : null}{s.stats ? <> · {s.stats.ownerTempPassword ? 'temporary password' : s.stats.ownerPasswordChanged ? 'password changed ' + fmtDate(String(s.stats.ownerPasswordChanged).slice(0, 10)) : 'password set'}</> : null}{s.owner_email ? ' · ' + s.owner_email : ''}</small></>,
                address(s) ? <code className="addr">{address(s)}</code> : <span className="muted">Set an address</span>,
                <><Badge tone={s.status === 'active' ? 'green' : s.status === 'trial' ? 'blue' : 'red'}>{s.status === 'trial' ? 'Trial' : s.status === 'active' ? 'Active' : 'Paused'}</Badge>
                  {s.status === 'trial' && s.trial_ends && <small className={s.trial_ends < today() ? 'danger' : 'muted'}>ends {fmtDate(s.trial_ends)}</small>}</>,
                <>{s.plan}<small className="muted">{s.price ? dollars(s.price) + '/mo' : 'free'}</small></>,
                s.stats?.users ?? '—', s.stats?.openJobs ?? '—', s.stats ? dollars(s.stats.billedThisMonth) : '—',
                s.stats?.lastActivity ? ago(s.stats.lastActivity) : <span className="muted">never</span>,
                <span className="row-actions">
                  {s.status !== 'paused' && <Button kind="ghost" onClick={() => setModal({ open: s })}>Open shop</Button>}
                  <Button kind="ghost" onClick={() => setModal({ reset: s })}>Owner password</Button>
                  <Button kind="ghost" onClick={() => setModal({ backups: s })}>Backups</Button>
                  <button className="icon-btn" title="Edit" onClick={() => setModal({ edit: s })}><Icon name="edit" size={16} /></button>
                </span>,
              ])} />
          </Panel>
        )}
        <Leads />
        <Panel title="Recent activity">
          <ul className="list small">{(activity.data || []).map((a, i) => <li key={i}><span>{a.action}</span><span className="muted right">{a.name} · {ago(a.created_at)}</span></li>)}</ul>
        </Panel>
      </main>
      {modal === 'add' && <AddShop domain={me.domain} onClose={() => setModal(null)} onDone={() => { setModal(null); toast('Shop created — send the owner their address, username and password'); reload(); activity.reload(); }} />}
      {modal && typeof modal === 'object' && 'edit' in modal && <EditShop s={modal.edit} onClose={() => setModal(null)} onDone={() => { setModal(null); toast('Saved'); reload(); activity.reload(); }} />}
      {modal && typeof modal === 'object' && 'reset' in modal && (
        <Modal title={`Reset owner password — ${modal.reset.name}?`} onClose={() => setModal(null)}>
          <Form submitLabel="Make a temporary password" onCancel={() => setModal(null)} onSubmit={async () => {
            const r = await api<{ login: string; password: string }>(`/api/platform/shops/${modal.reset.id}/reset-owner`, { body: {} });
            setModal({ temp: { name: modal.reset.name, login: r.login, password: r.password } }); reload(); activity.reload();
          }}>
            <p className="full">The owner is signed out everywhere and gets a temporary password. At sign-in they choose their own — nobody, including you, can see it.</p>
          </Form>
        </Modal>
      )}
      {modal && typeof modal === 'object' && 'backups' in modal && <BackupsModal shop={modal.backups} onClose={() => { setModal(null); activity.reload(); }} />}
      {modal && typeof modal === 'object' && 'temp' in modal && (
        <Modal title={`Temporary password — ${modal.temp.name}`} onClose={() => setModal(null)}>
          <div className="modal-pad">
          <p>Username: <b>{modal.temp.login}</b></p>
          <div className="temp-pass">{modal.temp.password}</div>
          <p className="form-note">Give it to the owner by phone. It's shown only now.</p>
          <div className="actions"><Button kind="primary" onClick={() => { void navigator.clipboard?.writeText(modal.temp.password).then(() => toast('Copied')); }}>Copy</Button><Button onClick={() => setModal(null)}>Done</Button></div></div>
        </Modal>
      )}
      {modal && typeof modal === 'object' && 'open' in modal && (
        <Modal title={`Open ${modal.open.name}`} onClose={() => setModal(null)}>
          <div className="modal-pad"><p>This browser signs in to the shop for 4 hours. Use <b>Exit</b> on the yellow bar when you're done.</p>
          <div className="open-choices">
            <button className="open-choice" onClick={() => void openShop(modal.open, 'view')}><b>View only</b><span>Look at everything, change nothing. Not shown to the shop.</span></button>
            <button className="open-choice" onClick={() => void openShop(modal.open, 'edit')}><b>Edit</b><span>Fix things for them. Changes show in their log as “Asena Bay support”.</span></button>
          </div></div>
        </Modal>
      )}
      {modal === 'mypw' && (
        <Modal title="Change your password" onClose={() => setModal(null)}>
          <Form submitLabel="Change password" onCancel={() => setModal(null)} onSubmit={async v => {
            if (v.password !== v.password2) throw new Error('New passwords do not match');
            await api('/api/platform/password', { body: { current: v.current, password: v.password } }); setModal(null); toast('Password changed'); activity.reload();
          }}>
            <Field label="Current password" name="current" type="password" required full autoComplete="current-password" />
            <Field label="New password" name="password" type="password" minLength={6} required full autoComplete="new-password" />
            <Field label="Repeat new password" name="password2" type="password" minLength={6} required full autoComplete="new-password" />
          </Form>
        </Modal>
      )}
    </div>
  );
}

function AddShop({ domain, onClose, onDone }: { domain: string; onClose: () => void; onDone: () => void }) {
  const [slug, setSlug] = useState('');
  const [status, setStatus] = useState('trial');
  return (
    <Modal title="Add shop" onClose={onClose} wide>
      <Form submitLabel="Create shop" onCancel={onClose} onSubmit={async v => {
        await api('/api/platform/shops', { body: { ...v, price: toCents(v.price), trial_days: Number(v.trial_days || 0), temporary: true } }); onDone();
      }}>
        <h4 className="form-sub full">Shop</h4>
        <Field label="Shop name" name="name" required placeholder="Bob's Diesel & Trailer" onBlur={e => !slug && setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40))} />
        <Field label="Short name (for the address)" name="slug" required value={slug} onChange={e => setSlug(e.target.value.toLowerCase())} pattern="[a-z0-9]+(-[a-z0-9]+)*" hint={domain ? `Customer links: ${slug || 'short-name'}.${domain} · staff sign in at app.${domain}` : 'Lowercase letters, numbers, dashes'} />
        <Field label="Own address (optional)" name="domain" placeholder="app.theirshop.com" hint="If the shop uses its own domain. Point it to the platform in Cloudflare." />
        <Field label="Phone" name="owner_phone" type="tel" />
        <Field label="Address" name="address" full />
        <h4 className="form-sub full">Owner account</h4>
        <Field label="Owner name" name="owner_name" required />
        <Field label="Owner email" name="owner_email" type="email" />
        <Field label="Username" name="owner_login" required autoCapitalize="none" autoComplete="off" />
        <Field label="Temporary password" name="owner_password" minLength={6} required autoComplete="off" hint="They choose their own at first sign-in — you won't see it." />
        <h4 className="form-sub full">Subscription</h4>
        <Select label="Status" name="status" value={status} onChange={e => setStatus(e.target.value)}><option value="trial">Free trial</option><option value="active">Active (paying)</option></Select>
        {status === 'trial' ? <Field label="Trial length (days)" name="trial_days" type="number" min={0} max={365} defaultValue={60} /> : <span />}
        <Field label="Plan" name="plan" defaultValue="standard" />
        <MoneyField label="Price per month" name="price" />
        <TextArea label="Notes" name="notes" />
      </Form>
    </Modal>
  );
}

function EditShop({ s, onClose, onDone }: { s: Row; onClose: () => void; onDone: () => void }) {
  return (
    <Modal title={`Edit ${s.name}`} onClose={onClose} wide>
      <Form onCancel={onClose} onSubmit={async v => { await api(`/api/platform/shops/${s.id}`, { method: 'PATCH', body: { ...v, price: toCents(v.price) } }); onDone(); }}>
        <Field label="Shop name" name="name" required defaultValue={s.name} />
        <Field label="Own address" name="domain" defaultValue={s.domain || ''} placeholder="app.theirshop.com" />
        <Select label="Status" name="status" defaultValue={s.status} hint="Paused shops can't sign in or use the app. Their data is kept.">
          <option value="trial">Free trial</option><option value="active">Active (paying)</option><option value="paused">Paused</option>
        </Select>
        <Field label="Trial ends" name="trial_ends" type="date" defaultValue={s.trial_ends || ''} />
        <Field label="Plan" name="plan" defaultValue={s.plan} />
        <MoneyField label="Price per month" name="price" defaultValue={centsInput(s.price)} />
        <Field label="Owner name" name="owner_name" defaultValue={s.owner_name} />
        <Field label="Owner email" name="owner_email" type="email" defaultValue={s.owner_email} />
        <Field label="Phone" name="owner_phone" defaultValue={s.owner_phone} />
        <TextArea label="Notes" name="notes" defaultValue={s.notes} />
        <p className="form-note full">Short name: <b>{s.slug}</b> (can't be changed). Created {fmtDate(s.created_at)}.</p>
      </Form>
    </Modal>
  );
}

/** "Contact us" requests from the asenabay.com website: call them, then mark what happened. */
function Leads() {
  const { data, reload } = useApi<Row[]>('/api/platform/leads');
  const toast = useToast();
  const list = data || [];
  const fresh = list.filter(l => l.status === 'new').length;
  const set = async (l: Row, body: Row) => { await api(`/api/platform/leads/${l.id}`, { method: 'PATCH', body }); toast('Saved'); reload(); };
  return (
    <Panel title={`Website requests${fresh ? ` · ${fresh} new` : ''}`}>
      <Table heads={['When', 'Name / shop', 'Phone', 'Email', 'Mechanics', 'Message', 'Status']} empty="No requests from the website yet."
        rows={list.map(l => [
          <small>{ago(l.created_at)}</small>,
          <><b>{l.name}</b><small className="muted">{l.shop}</small></>,
          l.phone ? <a href={`tel:${l.phone}`}>{l.phone}</a> : '—',
          l.email ? <a href={`mailto:${l.email}`}>{l.email}</a> : '—',
          l.mechanics || '—',
          <small>{l.message || ''}</small>,
          <select className="mini-select" value={l.status} aria-label="Status" onChange={e => void set(l, { status: e.target.value })}>
            <option value="new">New — call them</option><option value="called">Called</option><option value="won">Signed up</option><option value="lost">Not interested</option>
          </select>,
        ])} />
    </Panel>
  );
}

/** Nightly copies of one shop: back up now, download, or put the shop back to a day (a copy of "now" is taken first). */
function BackupsModal({ shop, onClose }: { shop: Row; onClose: () => void }) {
  const { data, reload } = useApi<Row[]>(`/api/platform/shops/${shop.id}/backups`);
  const [restore, setRestore] = useState<Row | null>(null);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  const kb = (n: number) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);
  return (
    <Modal title={`Backups — ${shop.name}`} onClose={onClose} wide>
      <div className="modal-pad">
        {!restore ? <>
          <p>Every night at ~3:40 AM the whole shop is copied. Kept 90 days, plus the 1st of every month for a year. Photos are stored separately and are not affected by a restore.</p>
          <div className="actions"><Button kind="primary" busy={busy} onClick={async () => { setBusy(true); try { await api(`/api/platform/shops/${shop.id}/backup-now`, { body: {} }); toast('Backup made'); reload(); } catch (e) { toast((e as Error).message, 'err'); } finally { setBusy(false); } }}>Back up now</Button></div>
          {!data ? <Loading /> : <Table heads={['When', 'Kind', 'Size', '']} empty="No backups yet — the first one runs tonight (or press Back up now)."
            rows={data.map(b => [b.created_at.slice(0, 16).replace('T', ' ') + ' UTC', b.kind === 'before-restore' ? 'before a restore' : b.kind, kb(b.size),
              <span className="row-actions"><a className="btn ghost" href={`/api/platform/backups/${b.id}/download`}>Download</a><Button kind="ghost" onClick={() => setRestore(b)}>Restore…</Button></span>])} />}
        </> : (
          <Form submitLabel="Restore the shop to this day" danger onCancel={() => setRestore(null)} onSubmit={async v => {
            const r = await api<{ rows: number }>(`/api/platform/shops/${shop.id}/restore`, { body: { backup_id: restore.id, confirm: v.confirm } });
            toast(`Restored (${r.rows} records). Everyone in the shop must sign in again.`); setRestore(null); reload();
          }}>
            <p className="full">This puts <b>{shop.name}</b> back to <b>{restore.created_at.slice(0, 16).replace('T', ' ')} UTC</b>. Anything entered after that is replaced — but a copy of how the shop is right now is saved first, so you can undo it.</p>
            <Field label={`Type the short name (${shop.slug}) to confirm`} name="confirm" required full autoComplete="off" />
          </Form>
        )}
      </div>
    </Modal>
  );
}
